import React, { useState, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import Papa from 'papaparse';
import {
    Box, Chip, FormControl, InputLabel, Link, MenuItem, Paper, Select, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow,
} from '@mui/material';
import { vendorReport } from '../../actions/invoiceDateActions';
import {
    CountTile, ReportError, ReportFilters, ReportPagination, ReportState, downloadCsv, formatDateToYYYYMMDD, pageSlice,
} from '../../components/common/ReportParts';

const VendorReports = () => {
    const dispatch = useDispatch();
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [reportData, setReportData] = useState([]);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [typeFilter, setTypeFilter] = useState('All');
    const [hasSearched, setHasSearched] = useState(false);

    const { filteredData, totalCount, arsCount, d2dCount } = useMemo(() => {
        let ars = 0;
        let d2d = 0;
        reportData.forEach((row) => {
            if (row.inputMethod === 'ARS') ars++;
            else d2d++;
        });
        const filtered = typeFilter === 'All' ? reportData : reportData.filter((row) => row.inputMethod === typeFilter);
        return { filteredData: filtered, totalCount: reportData.length, arsCount: ars, d2dCount: d2d };
    }, [reportData, typeFilter]);

    const handleGenerateReport = () => {
        if (!startDate || !endDate) {
            setError('Pick both a start and end date.');
            return;
        }
        setIsLoading(true);
        setError(null);
        setTypeFilter('All');
        setHasSearched(true);
        dispatch(vendorReport(formatDateToYYYYMMDD(startDate), formatDateToYYYYMMDD(endDate)))
            .then((invoiceData) => {
                setReportData(invoiceData || []);
                setIsLoading(false);
                setPage(0);
            })
            .catch(() => {
                setError("The report couldn't be loaded. Try again.");
                setIsLoading(false);
            });
    };

    const handleExport = () => {
        const suffix = typeFilter !== 'All' ? `_${typeFilter}` : '';
        downloadCsv(
            Papa.unparse(filteredData),
            `ARS_Usage_Report${suffix}_${formatDateToYYYYMMDD(startDate)}_${formatDateToYYYYMMDD(endDate)}.csv`
        );
    };

    const changeType = (value) => {
        setTypeFilter(value);
        setPage(0);
    };

    return (
        <>
            <ReportFilters
                startDate={startDate}
                endDate={endDate}
                onStartChange={setStartDate}
                onEndChange={setEndDate}
                onRun={handleGenerateReport}
                onExport={handleExport}
                running={isLoading}
                canExport={filteredData.length > 0}
            >
                <FormControl sx={{ minWidth: 150 }}>
                    <InputLabel id="ars-type">Type</InputLabel>
                    <Select labelId="ars-type" label="Type" value={typeFilter} onChange={(e) => changeType(e.target.value)}>
                        <MenuItem value="All">All types</MenuItem>
                        <MenuItem value="ARS">ARS only</MenuItem>
                        <MenuItem value="D2D">D2D only</MenuItem>
                    </Select>
                </FormControl>
            </ReportFilters>
            <ReportError message={error} />

            {reportData.length > 0 && !isLoading && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                    <CountTile label="Total tows" count={totalCount} active={typeFilter === 'All'} onClick={() => changeType('All')} />
                    <CountTile label="Requested through ARS" count={arsCount} active={typeFilter === 'ARS'} onClick={() => changeType('ARS')} />
                    <CountTile label="Direct to dispatch (D2D)" count={d2dCount} active={typeFilter === 'D2D'} onClick={() => changeType('D2D')} />
                </Box>
            )}

            <ReportState
                loading={isLoading}
                hasRun={hasSearched}
                empty={filteredData.length === 0}
                emptyMessage={typeFilter !== 'All' ? `No ${typeFilter} tows in this date range.` : undefined}
            />

            {!isLoading && filteredData.length > 0 && (
                <Paper variant="outlined">
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Invoice</TableCell>
                                    <TableCell>Service call date</TableCell>
                                    <TableCell>Pickup address</TableCell>
                                    <TableCell>Type</TableCell>
                                    <TableCell>Caller</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {pageSlice(filteredData, page, rowsPerPage).map((row, index) => (
                                    <TableRow hover key={`${row.invoiceId}-${index}`}>
                                        <TableCell>
                                            <Link
                                                component="button"
                                                underline="hover"
                                                onClick={() => window.open(`/invoice/${row.invoiceId}`, '_blank')}
                                                sx={{ fontWeight: 600 }}
                                            >
                                                {row.invoiceId}
                                            </Link>
                                        </TableCell>
                                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{row.serviceCallDate}</TableCell>
                                        <TableCell>{row.pickUpAddress}</TableCell>
                                        <TableCell>
                                            <Chip
                                                label={row.inputMethod}
                                                size="small"
                                                color={row.inputMethod === 'ARS' ? 'primary' : 'default'}
                                                variant={row.inputMethod === 'ARS' ? 'filled' : 'outlined'}
                                            />
                                        </TableCell>
                                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{row.callerName}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <ReportPagination
                        count={filteredData.length}
                        page={page}
                        rowsPerPage={rowsPerPage}
                        onPageChange={setPage}
                        onRowsPerPageChange={(value) => {
                            setRowsPerPage(value);
                            setPage(0);
                        }}
                    />
                </Paper>
            )}
        </>
    );
};

export default VendorReports;
